import {Injectable} from '@angular/core';
import {ServicesSocketService} from '../websocket/services-socket.service';
import {EventRecord, EventType} from '../../shared/models/event.model';
import {BehaviorSubject, ReplaySubject, Subscription} from 'rxjs';
import {EventEvent} from '../websocket/model/event.event';
import {MessageSource, WebSocketEvent} from '../websocket/model/message.event';
import {SettingsService} from '../../configuration/settings.service';
import { ConfigurationService } from '../../configuration/configuration.service';

const DISMISSED_EVENT_IDS_LOCALSTORAGE_KEY = 'dismissedEventIds';

const STARTING_EVENT_TYPE_FOR_ENDING_EVENT_TYPE = {
  [EventType.SHIFT_RESUMED]: EventType.SHIFT_PAUSED,
  [EventType.NO_LONGER_STATIONARY]: EventType.STATIONARY_ALERT
};

@Injectable({
  providedIn: 'root'
})
export class EventManagerService {

  public readonly events$ = new ReplaySubject<EventRecord[]>(1);
  public readonly eventsNotReadCount$ = new BehaviorSubject<number>(0);
  statusEventSubscription: Subscription;
  configurationSubscription: Subscription;
  private isInitialized = false;
  private eventRecords: EventRecord[] = [];
  private lastEventReadTime = 0;
  private userDismissedEventIds = {};
  private contentVisibilityCutoff: Date = null;

  constructor(
    private readonly servicesSocketService: ServicesSocketService,
    private readonly settingsService: SettingsService,
    private readonly configService: ConfigurationService,
  ) {
  }

  public init(
    eventRecordsSortedCreatedDesc: EventRecord[],
  ) {
    if (this.isInitialized) {
      throw Error('The EventManagerService has already been initialized.');
    }
    this.isInitialized = true;

    this.userDismissedEventIds = this.getDismissedEventIdsAsMap();

    // initialize records
    const lastReadTimeStr = this.settingsService.getStringValue(SettingsService.EVENTS_LAST_READ_TIME);
    if (lastReadTimeStr) {
      this.lastEventReadTime = Date.parse(lastReadTimeStr);
    }

    const {dismissedEventIdsUsed} = this.processRecentEventRecords(eventRecordsSortedCreatedDesc, this.userDismissedEventIds);

    if (dismissedEventIdsUsed !== this.userDismissedEventIds) {
      this.userDismissedEventIds = dismissedEventIdsUsed;
      this.updateDismissedEventIds();
    }

    // initialize settings
    this.statusEventSubscription = this.servicesSocketService
      .onMessage(MessageSource.EVENT)
      .subscribe((eventEvent: WebSocketEvent<EventEvent, EventRecord>) => {
        this.processNewEvent(eventEvent.data);
      });

    this.configurationSubscription = this.configService.sharedConfigurationModel.subscribe(model => {
      if (model) {
        this.contentVisibilityCutoff = model.contentVisibilityCutoff;
        this.eventsNotReadCount$.next(this.getUnreadEvents().length);
      }
    });
  }

  processRecentEventRecords(eventRecordsSortedCreatedDesc: EventRecord[], dismissedEventIds: {
    [key: number]: boolean
  }): { events: EventRecord[], dismissedEventIdsUsed: { [key: number]: boolean } } {
    function handleEventPair(event: EventRecord, startEventType: EventType, endEventType: EventType, flags: {}): boolean {
      if (event.name === endEventType) {
        flags[event.shiftId] = true;
        return true;
      }
      if (event.name === startEventType && flags[event.shiftId]) {
        // paired end with start
        delete flags[event.shiftId];
        return true;
      }
      return false;
    }

    const dismissedEventIdsUsed = {};
    const unpausedShifts = {};
    const noLongerStationaryShifts = {};

    this.eventRecords = eventRecordsSortedCreatedDesc.filter(event => {
      if (handleEventPair(event, EventType.SHIFT_PAUSED, EventType.SHIFT_RESUMED, unpausedShifts) ||
        handleEventPair(event, EventType.STATIONARY_ALERT, EventType.NO_LONGER_STATIONARY, noLongerStationaryShifts)) {
        return false;
      }

      if (dismissedEventIds[event.id]) {
        dismissedEventIdsUsed[event.id] = true;
        return false;
      }
      return true;
    });
    this.onEventRecordsUpdated();

    return {
      events: this.eventRecords,
      dismissedEventIdsUsed
    };
  }

  public release() {
    this.eventRecords = [];
    this.statusEventSubscription?.unsubscribe();
    this.isInitialized = false;
  }

  public setAllEventsReadUpToNow() {
    let now = new Date();
    this.settingsService.setStringValue(SettingsService.EVENTS_LAST_READ_TIME, now.toISOString());
    this.lastEventReadTime = now.getTime();
    this.eventsNotReadCount$.next(0);
  }

  public dismissEvent(eventId: number) {
    this.userDismissedEventIds[eventId] = true;
    this.updateDismissedEventIds();

    this.eventRecords = this.eventRecords.filter(record => record.id !== eventId);
    this.onEventRecordsUpdated();
  }

  processNewEvent(newEvent: EventRecord) {
    if (this.userDismissedEventIds[newEvent.id]) {
      return;
    }
    const matchingEventType = STARTING_EVENT_TYPE_FOR_ENDING_EVENT_TYPE[newEvent.name];
    if (matchingEventType) {
      const indexOfMatchingEvent = this.eventRecords
        .findIndex(event => event.shiftId === newEvent.shiftId && event.name === matchingEventType);
      if (indexOfMatchingEvent === -1) {
        return;
      }
      this.eventRecords.splice(indexOfMatchingEvent, 1);
    } else {
      // add as the first
      this.eventRecords.splice(0, 0, newEvent);
    }

    this.onEventRecordsUpdated();
  }

  private getUnreadEvents() {
    return this.eventRecords
      .filter(eventRecord => {
        const eventCreated = new Date(eventRecord.created).getTime();
        return eventCreated > this.lastEventReadTime &&
          (!this.contentVisibilityCutoff || eventCreated > new Date(this.contentVisibilityCutoff).getTime());
      });
  }

  private onEventRecordsUpdated() {
    // update event records
    this.events$.next(this.eventRecords);
    // update unread count
    this.eventsNotReadCount$.next(this.getUnreadEvents().length);
  }

  private updateDismissedEventIds() {
    localStorage.setItem(DISMISSED_EVENT_IDS_LOCALSTORAGE_KEY,
      JSON.stringify(Object.keys(this.userDismissedEventIds).map(key => Number(key)))
    );
  }

  private getDismissedEventIdsAsMap() {
    const eventIds = (JSON.parse(localStorage.getItem(DISMISSED_EVENT_IDS_LOCALSTORAGE_KEY)) as number[]) || [];
    return eventIds.reduce((ids, eventId) => {
      ids[eventId] = true;
      return ids;
    }, {});
  }
}
